/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import { isNil } from 'lodash';
import { Item, Shape } from 'paper';
import {
  getGraphicalTakeOffColor,
  getGraphicalTakeOffColorPalette,
  getGraphicalTakeOffMeasurementId,
  getGraphicalTakeOffTopViewId,
  GraphicalTakeOffMeasurement,
  GraphicalTakeOffTopView,
  GraphicalTakeOffViewTransform,
} from '../../resources/models';
import {
  constantScalePaperEffect,
  moveOnFront,
  moveOnBack,
  toPaperPoint,
  visiblePaperEffect,
} from './canvas.utils';
import {
  GraphicalTakeOffElementBase,
  paperEffect,
  PaperElementEffect,
} from './graphical-take-off-element';

const measurementPaperEffects: PaperElementEffect[] = [
  paperEffect('visible', visiblePaperEffect),
  paperEffect(['selected', 'dataBoxVisible'], (item: Item, [selected, dataBoxVisible]: [boolean, boolean]) => {
    item.selected = dataBoxVisible ? false : selected;
    if (selected) {
      moveOnFront(item, 'measurement');
    } else {
      moveOnBack(item, 'measurement');
    }
  }),
  paperEffect('viewTransform', constantScalePaperEffect),
  paperEffect(['measurement', 'viewTransform'], (item,
                                                 [measurement, viewTransform],
                                                 [previousMeasurement, previousViewTransform]) => {
    const { points: [point] } = measurement;
    if (isNil(previousMeasurement) || measurement.points !== previousMeasurement.points || viewTransform !== previousViewTransform) {
      item.position = toPaperPoint(point);
    }
  }),
];

@Component({
  standalone: false,
  selector: 'gh-graphical-take-off-canvas-count-measurement',
  templateUrl: 'graphical-take-off-canvas-count-measurement.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GraphicalTakeOffCanvasCountMeasurementComponent extends GraphicalTakeOffElementBase {
  @Input() top: GraphicalTakeOffTopView;
  @Input() measurement: GraphicalTakeOffMeasurement;
  @Input() visible: boolean;
  @Input() viewTransform: GraphicalTakeOffViewTransform;
  @Input() dataBoxVisible: boolean;
  @Input() highlightedDataBoxId?: ID;
  @Input() selected: boolean;

  constructor() {
    super(measurementPaperEffects);
  }

  protected createPaperItem(): Item {
    const { measurement: { points: [point] }, top } = this;
    const color = getGraphicalTakeOffColor(top);
    const palette = getGraphicalTakeOffColorPalette(color);
    const circle = Shape.Circle(toPaperPoint(point), 5);
    circle.strokeColor = palette.primary;
    circle.fillColor = palette.background;
    circle.data = {
      topId: getGraphicalTakeOffTopViewId(this.top),
      measurementId: getGraphicalTakeOffMeasurementId(this.measurement),
      type: 'measurement',
    };
    moveOnBack(circle, 'measurement');
    return circle;
  }
}
